/**
 * lx-camera-view —— 摄像头预览覆盖层
 *
 * 真预览（getUserMedia 流），不是静态图。这一层的存在是为了让
 * 「打开摄像头」这个指令有**看得见的结果** —— 用户说一句话，
 * 屏幕上真的出现了实时画面，这是本 PoC 最有说服力的演示之一。
 *
 * 流由 device-runtime 统一持有（API 层打开的），本组件只负责把流挂到 <video>。
 * 组件销毁时**不**停止流 —— 因为流的生命周期归 API 管，
 * 用户可能「关闭预览但保持摄像头开着」以便后续拍照。
 * 真正的关闭由 device.cameraClose() 负责。
 *
 * M5 补：从聊天页的加号菜单进来时（`attach-target` 里有待发送目标），
 * 底部会多出一个「拍照并发送」按钮 —— 拍完直接写进那个会话，
 * 不需要再对 AI 说一句「发给谁」。两条进入路径的区别：
 *   · AI 说「打开摄像头」       → 只看预览，无拍照按钮（下一步由用户自己说）
 *   · 聊天页点「拍照」          → 预览 + 拍照按钮，拍完即发
 */

import { LxElement, define } from '@/core/base-element';
import { deviceRuntime } from '@/il/device-runtime';
import { popOverlay } from '@/core/ui-store';
import { takeAttachTarget, clearAttachTarget, hasAttachTarget } from '@/core/attach-target';
import { filesRepo } from '@/data/repo.files';
import { trace } from '@/ai/trace';

export class LxCameraView extends LxElement {
  /** 是不是「聊天页发起的拍照」。决定要不要显示拍照按钮 */
  private _fromChat = false;

  protected styles(): string {
    return `
      :host {
        position: absolute;
        inset: 0;
        z-index: var(--z-overlay);
        display: flex;
        flex-direction: column;
        background: #000;
      }

      video {
        flex: 1;
        width: 100%;
        min-height: 0;
        object-fit: contain;
        background: #000;
      }

      .bar {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: var(--sp-3);
        padding: var(--sp-3) var(--layout-content-pad-x);
        padding-bottom: calc(var(--sp-3) + env(safe-area-inset-bottom, 0px));
        background: rgba(0, 0, 0, .7);
      }

      .msg {
        flex: 1;
        color: #eee;
        font-size: var(--fs-sm);
      }

      button {
        border: none;
        background: var(--c-primary);
        color: var(--c-on-primary);
        font-size: var(--fs-sm);
        padding: var(--sp-2) var(--sp-4);
        border-radius: var(--radius-full);
        cursor: pointer;
      }
      button.ghost {
        background: rgba(255,255,255,.14);
        color: #fff;
      }
      button.shutter {
        background: #fff;
        color: #111;
        font-weight: var(--fw-medium);
      }
      button:disabled { opacity: .5; cursor: default; }

      .placeholder {
        margin: auto;
        color: #aaa;
        font-size: var(--fs-sm);
        text-align: center;
        padding: var(--sp-6);
        line-height: var(--lh-relaxed, 1.7);
      }
    `;
  }

  protected render(): string {
    const on = deviceRuntime.isCameraOn;
    return `
      ${on ? `<video autoplay playsinline muted></video>` : `
        <div class="placeholder">
          摄像头没有打开。<br>对输入栏说「打开摄像头」就能看到画面。
        </div>
      `}
      <div class="bar">
        <div class="msg">${on ? (this._fromChat ? '拍一张发出去' : '摄像头预览中') : '未开启'}</div>
        ${this._fromChat && on ? `<button class="shutter" data-shoot>拍照并发送</button>` : ''}
        <button class="ghost" data-close>关闭预览</button>
      </div>
    `;
  }

  protected onMounted(): void {
    const video = this.$('video') as HTMLVideoElement | null;
    if (video && deviceRuntime.camera.stream) {
      video.srcObject = deviceRuntime.camera.stream;
    }

    // 离开这一层时若还有未消费的目标，清掉 ——
    // 否则下次从别的入口打开摄像头会误发给上一次那个会话。
    this.$('[data-close]')?.addEventListener('click', () => {
      clearAttachTarget();
      popOverlay();
    });

    this.$('[data-shoot]')?.addEventListener('click', () => void this.shootAndSend());

    // 预览开关变化时重渲染（如用户对输入栏说「关闭摄像头」）
    this.subscribe('device:camera-changed', () => this.update());
  }

  /**
   * 拍一张并立刻发给目标会话。
   *
   * 复用 `file.sendPhoto` 那套「写 messages + files 两张表」的 repo 方法，
   * 而不是在这里再写一遍 —— 见 `filesRepo.attachToMessage` 的注释。
   */
  private async shootAndSend(): Promise<void> {
    const target = takeAttachTarget();
    if (!target) {
      // 不该发生：按钮只在 _fromChat 时渲染，而 _fromChat 依赖 target 存在。
      // 真走到了说明目标被别处清掉了，如实退出而不是猜一个会话发过去。
      trace.note('✗ 拍照发送：没有待发送目标，已取消');
      return;
    }

    const stream = deviceRuntime.camera.stream;
    if (!stream) return;

    const btn = this.$('[data-shoot]') as HTMLButtonElement | null;
    if (btn) btn.disabled = true;

    try {
      const blob = await captureFrame(stream);
      const name = `照片_${stamp()}.png`;
      deviceRuntime.camera.lastPhoto = blob;
      deviceRuntime.camera.lastPhotoName = name;

      await filesRepo.attachToMessage({
        sessionId: target.sessionId,
        sender: 'me',
        name,
        blob,
        mime: 'image/png',
        kind: 'image',
      });

      trace.note(`✓ 已拍照并发送（${Math.round(blob.size / 1024)} KB）`);
      popOverlay();
    } catch (err) {
      trace.note(`✗ 拍照发送失败：${err instanceof Error ? err.message : String(err)}`);
      if (btn) btn.disabled = false;
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    // 进入这一层时判定一次来源 —— 之后不再变（take 会清空目标）
    this._fromChat = hasAttachTarget();
  }
}

/** 从视频流抓一帧存成 PNG blob */
async function captureFrame(stream: MediaStream): Promise<Blob> {
  const video = document.createElement('video');
  video.srcObject = stream;
  video.muted = true;
  await video.play();

  const track = stream.getVideoTracks()[0];
  const settings = track?.getSettings?.() ?? {};
  const w = settings.width ?? video.videoWidth ?? 640;
  const h = settings.height ?? video.videoHeight ?? 480;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法创建画布来保存照片');

  ctx.drawImage(video, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));

  video.pause();
  video.srcObject = null;

  if (!blob) throw new Error('照片生成失败');
  return blob;
}

/** 文件名用的时间戳：2026-10-15-01-20-33 */
function stamp(): string {
  return new Date().toLocaleString('zh-CN').replace(/[/: ]/g, '-');
}

define('lx-camera-view', LxCameraView);
